
import { defineComponent, PropType, ref, toRef, watch } from "vue";
import ValidationFormErrors from "@/Components/Forms/ValidationFormErrors.vue";
import HcButton from "@/Components/Buttons/Button.vue";
import SvgIcon from "@jamescoyle/vue-icon";
import Card from "@/Components/General/Card.vue";
import Modal from "@/Components/General/Modal.vue";
import { mdiContentSave, mdiLoading, mdiTrashCan } from "@mdi/js";
import RfpCategory from "@/types/Rfp/RfpCategory";
import { useForm } from "@inertiajs/inertia-vue3";
import route from "ziggy-js";
import {Inertia} from "@inertiajs/inertia";

interface Props {
  category?: RfpCategory;
}

interface RfpCategoryForm {
  title: string;
}

export default defineComponent({
  props: {
    category: Object as PropType<RfpCategory>,
  },
  emits: ["cancel", "deleted"],
  components: {
    ValidationFormErrors,
    HcButton,
    SvgIcon,
    Card,
    Modal,
  },
  setup(props: Props, { emit }) {
    const category = toRef(props, "category");
    const showDeleteModal = ref(false);

    const form = ref(
      useForm<RfpCategoryForm>({
        title: category.value?.title ? category.value?.title : "",
      })
    );

    function resetForm() {
      form.value.reset();
      form.value.clearErrors();
      form.value.title = category.value?.title ?? "";
    }

    watch(showDeleteModal, function () {
      resetForm();
    });

    watch(
      () => category.value?.id,
      function () {
        form.value = useForm<RfpCategoryForm>({
          title: category.value?.title ? category.value?.title : "",
        });
      }
    );

    function submit() {
      if (category.value) {
        form.value.patch(
          route("rfp-categories.update", {
            rfp_category: category.value.id,
          }),
          {
            onSuccess: () => {
              resetForm();
            },
          }
        );
      } else {
        form.value.post(route("rfp-categories.store"), {
          onSuccess: () => {
            resetForm();
          },
        });
      }
    }

    function cancelCreate() {
      resetForm();
      emit("cancel");
    }

    function deleteCategory() {
      if (!category?.value?.id) {
        return;
      }

      Inertia.delete(
        route("rfp-categories.destroy", {
          rfp_category: category.value.id,
        }),
        {
          onSuccess: () => {
            showDeleteModal.value = false;

            emit("deleted");
          },
        }
      );
    }

    return {
      saveIcon: mdiContentSave,
      loadingIcon: mdiLoading,
      deleteIcon: mdiTrashCan,
      form,
      showDeleteModal,
      submit,
      cancelCreate,
      deleteCategory,
    };
  },
});
